import { apiV2, errorHandler } from './index';
import {
  API_ERROR,
  FETCH_ALL_EXPERIMENTS,
  FETCH_EXPERIMENT_SUMMARY,
} from '../constants/ActionTypes';
import Swal from 'sweetalert2';

// A/B experiments management. Backed by the APIv2 admin endpoints:
//   GET  /admin/experiments
//   POST /admin/experiments
//   PUT  /admin/experiments/{slug}
//   GET  /admin/experiments/{slug}/summary
//
// PUT /admin/experiments/{slug} also accepts the partial body
// { status: 'decided', winning_variant } — see declareExperimentWinner.

export function fetchAllExperiments() {
  return function (dispatch) {
    apiV2.get('/admin/experiments')
      .then(response => {
        dispatch({
          type: FETCH_ALL_EXPERIMENTS,
          payload: response.data.experiments,
        });
      })
      .catch(error => {
        errorHandler(dispatch, error.response, API_ERROR);
      });
  };
}

// Per-variant assignment counts for the edit page's live-progress panel.
export function fetchExperimentSummary(slug) {
  return function (dispatch) {
    dispatch({ type: FETCH_EXPERIMENT_SUMMARY, payload: null });
    apiV2.get(`/admin/experiments/${slug}/summary`)
      .then(response => {
        dispatch({ type: FETCH_EXPERIMENT_SUMMARY, payload: response.data });
      })
      .catch(error => {
        errorHandler(dispatch, error.response, API_ERROR);
      });
  };
}

// Declares a winner via the dedicated status: 'decided' transition. Only
// allowed while the experiment is running or paused; the server 422s
// otherwise. Kept separate from saveExperiment because it sends a
// partial body (status + winning_variant only), not the full form payload.
//
// Unlike the other thunks here this one RESOLVES A RESULT — `{ ok: true }`
// or `{ ok: false, message }` — instead of resolving identically on
// success and failure. DeclareWinnerModal stays open on the operator's
// screen while the request is in flight, so it has to be able to tell the
// two apart: only a success may close the dialog and navigate away.
//
// The failure path deliberately does NOT call `errorHandler`. Its Swal
// would stack a second dialog on top of the still-open modal saying the
// same thing in the wrong place; the message is handed back to the caller
// and rendered inline in the dialog instead, next to the selection that
// was kept. `errorHandler` itself is untouched — every other thunk in the
// repo keeps using it.
export function declareExperimentWinner(slug, winningVariant, onSuccess) {
  return function () {
    return apiV2.put(`/admin/experiments/${slug}`, {
      status: 'decided',
      winning_variant: winningVariant,
    })
      .then(() => {
        // Not awaited: the caller closes the dialog on this result, and
        // the toast + navigation run over the page behind it.
        Swal.fire({
          icon: 'success',
          title: 'Saved!',
          timer: 1000,
        }).then(() => {
          onSuccess?.();
        });
        return { ok: true };
      })
      .catch(error => {
        const response = error?.response;
        // A 401 has already been handled globally (logout + its own
        // dialog + a redirect to /login). Reporting it inline as well
        // would contradict that, so fail quietly.
        if (error?._sessionExpired || response?._sessionExpired) {
          return { ok: false };
        }
        return {
          ok: false,
          message: response?.data?.message || 'Could not declare the winner. Please try again.',
        };
      });
  };
}

// Create (no slug yet) or update (by slug) an experiment.
export function saveExperiment(experiment_json, onSuccess) {
  return function (dispatch) {
    const data = typeof experiment_json === 'string'
      ? JSON.parse(experiment_json)
      : experiment_json;

    // `isEdit` only drives create-vs-update routing; keep it out of the body.
    const { isEdit, ...payload } = data;
    const request = isEdit
      ? apiV2.put(`/admin/experiments/${payload.slug}`, payload)
      : apiV2.post('/admin/experiments', payload);

    // Return the promise so the caller's spinner clears on actual
    // completion rather than a fixed timeout.
    return request
      .then(() => {
        Swal.fire({
          icon: 'success',
          title: 'Saved!',
          timer: 1000,
        }).then(() => {
          onSuccess?.();
        });
      })
      .catch(error => {
        errorHandler(dispatch, error.response, API_ERROR);
      });
  };
}
